'use client'

import { Tooltip } from '@trustoo/components/Tooltip/v2'
import clsx from 'clsx'
import { ComponentPropsWithoutRef, FocusEvent, forwardRef, ReactNode, useId, useState } from 'react'

export type TextareaSize = 'sm' | 'md'
export type TextareaVariant = 'default' | 'tags-inner' | 'tags-outer' | 'tags'
export type TextareaState = 'placeholder' | 'default' | 'focused' | 'disabled'

export interface TextareaProps extends Omit<ComponentPropsWithoutRef<'textarea'>, 'size'> {
    size?: TextareaSize
    variant?: TextareaVariant
    state?: TextareaState
    destructive?: boolean
    label?: ReactNode
    hintText?: ReactNode
    required?: boolean
    helpIcon?: boolean
    helpText?: ReactNode
    resizeHandle?: boolean
    tags?: ReactNode
    error?: string | boolean
    defaultErrorMsg?: string
    withErrorIcon?: boolean
    className?: string
    textareaClassName?: string
    labelClassName?: string
    errorClassName?: string
}

interface SizeClasses {
    box: string
    boxTagsInner: string
    text: string
    hint: string
}

const SIZE_CLASSES: Record<TextareaSize, SizeClasses> = {
    sm: {
        box: 'min-h-[114px] px-spacing-lg py-spacing-lg',
        boxTagsInner: 'min-h-[114px] p-spacing-lg',
        text: 'text-sm',
        hint: 'text-xs',
    },
    md: {
        box: 'min-h-[132px] px-3.5 py-spacing-lg',
        boxTagsInner: 'min-h-[132px] p-spacing-lg',
        text: 'text-md',
        hint: 'text-sm',
    },
}

const HelpCircleIcon = () => (
    <svg className="size-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const AlertCircleIcon = () => (
    <svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d="M12 8v4M12 16h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
    (
        {
            size = 'sm',
            variant = 'default',
            state = 'placeholder',
            destructive = false,
            label,
            hintText,
            required = false,
            helpIcon = false,
            helpText,
            resizeHandle = true,
            tags,
            error,
            defaultErrorMsg,
            withErrorIcon = false,
            className,
            textareaClassName,
            labelClassName,
            errorClassName,
            id: providedId,
            disabled,
            onFocus: onFocusProp,
            onBlur: onBlurProp,
            ...restTextareaProps
        },
        ref,
    ) => {
        const generatedId = useId()
        const id = providedId || generatedId
        const hintId = `${id}-hint`
        const sizeClasses = SIZE_CLASSES[size]

        const [focused, setFocused] = useState(false)

        const isDisabled = Boolean(disabled) || state === 'disabled'
        const isFocused = !isDisabled && (focused || state === 'focused')
        const hasError = Boolean(error) || destructive
        const isTagsInner = variant === 'tags-inner' || variant === 'tags'
        const isTagsOuter = variant === 'tags-outer'

        const onFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
            if (!isDisabled) {
                setFocused(true)
            }
            onFocusProp?.(e)
        }

        const onBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
            setFocused(false)
            onBlurProp?.(e)
        }

        const getErrorMessage = (): string | undefined => {
            if (typeof error === 'string') {
                return error
            }
            if (error) {
                return defaultErrorMsg || 'This field has an error'
            }
            return undefined
        }
        const errorMessage = getErrorMessage()

        const getBorderClasses = () => {
            if (hasError && isFocused) {
                return 'border-border-error ring-1 ring-inset ring-focus-ring-error'
            }
            if (hasError) {
                return 'border-border-error-subtle'
            }
            if (isFocused) {
                return 'border-border-brand ring-1 ring-inset ring-focus-ring'
            }
            return 'border-border-primary'
        }

        const boxClasses = clsx(
            'w-full rounded-radius-md border border-solid bg-background-bg-primary shadow-xs',
            'transition-colors duration-150 ease-in-out',
            getBorderClasses(),
            isDisabled && 'cursor-not-allowed opacity-50',
        )

        const textareaBaseClasses = clsx(
            'font-body font-regular text-text-primary placeholder:text-text-placeholder',
            'outline-none disabled:cursor-not-allowed',
            sizeClasses.text,
            resizeHandle ? 'resize-y' : 'resize-none',
        )

        const textareaElement = (extraClasses: string) => (
            <textarea
                {...restTextareaProps}
                ref={ref}
                id={id}
                disabled={isDisabled}
                required={required}
                aria-invalid={hasError || undefined}
                aria-describedby={errorMessage || hintText ? hintId : undefined}
                onFocus={onFocus}
                onBlur={onBlur}
                className={clsx(textareaBaseClasses, extraClasses, textareaClassName)}
            />
        )

        const renderField = () => {
            if (isTagsInner) {
                return (
                    <div className={clsx('flex flex-col', boxClasses, sizeClasses.boxTagsInner)}>
                        <div className="flex flex-1 flex-wrap items-start gap-spacing-md">
                            {tags}
                            {textareaElement(
                                'min-w-20 flex-1 self-stretch border-0 bg-base-transparent p-spacing-none',
                            )}
                        </div>
                    </div>
                )
            }
            return textareaElement(clsx('block', boxClasses, sizeClasses.box))
        }

        const renderHelpIcon = () => {
            if (!helpIcon) {
                return null
            }
            if (helpText) {
                return (
                    <Tooltip title={helpText}>
                        <button
                            type="button"
                            aria-label="Help"
                            className="inline-flex cursor-help items-center rounded-radius-full border-0 bg-base-transparent p-spacing-none text-foreground-fg-quaternary hover:text-foreground-fg-quaternary_hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring">
                            <HelpCircleIcon />
                        </button>
                    </Tooltip>
                )
            }
            return (
                <span className="inline-flex cursor-help text-foreground-fg-quaternary">
                    <HelpCircleIcon />
                </span>
            )
        }

        const renderMessage = () => {
            if (errorMessage) {
                return (
                    <div
                        id={hintId}
                        className={clsx(
                            'flex items-center gap-spacing-xs font-regular text-text-error',
                            sizeClasses.hint,
                            errorClassName,
                        )}>
                        {withErrorIcon && <AlertCircleIcon />}
                        {errorMessage}
                    </div>
                )
            }
            if (hintText) {
                return (
                    <div
                        id={hintId}
                        className={clsx(
                            'font-regular',
                            sizeClasses.hint,
                            destructive ? 'text-text-error' : 'text-text-tertiary',
                        )}>
                        {hintText}
                    </div>
                )
            }
            return null
        }

        const showLabelRow = Boolean(label) || helpIcon

        return (
            <div className={clsx('flex w-full flex-col gap-spacing-sm font-body', className)}>
                {showLabelRow && (
                    <div className={clsx('flex items-center gap-spacing-xxs', labelClassName)}>
                        {label && (
                            <label htmlFor={id} className="text-sm font-medium text-text-secondary">
                                {label}
                            </label>
                        )}
                        {label && required && (
                            <span
                                className="text-sm font-medium text-text-brand-tertiary"
                                aria-hidden="true">
                                *
                            </span>
                        )}
                        {renderHelpIcon()}
                    </div>
                )}

                {renderField()}

                {isTagsOuter && tags && <div className="flex flex-wrap gap-spacing-sm">{tags}</div>}

                {renderMessage()}
            </div>
        )
    },
)

Textarea.displayName = 'Textarea'
